import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
  type StatusTone,
} from '@/components/ui'
import {
  abrirFolha,
  aprovarFolha,
  calcularFolha,
  fetchDpDashboard,
  fetchFerias,
  fetchFolhas,
  fetchRescisoes,
} from './api'
import type { DpTab } from './types'
import { formatCompetencia, formatDate, formatMoney } from './utils'

const num = (v: number | undefined) => (v ?? 0).toLocaleString('pt-BR')

function competenciaAtual() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function statusTone(status?: string): StatusTone {
  const s = (status || '').toLowerCase()
  if (/(pend|abert|aguard|rascunho|solicit)/.test(s)) return 'pendente'
  if (/(cancel|rejeit|recus|reprov|negad)/.test(s)) return 'inativo'
  return 'ativo'
}

function errMsg(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback
}

function TableState({
  loading,
  error,
  empty,
  emptyText,
  children,
}: {
  loading: boolean
  error: unknown
  empty: boolean
  emptyText: string
  children: ReactNode
}) {
  if (loading) return <Muted>Carregando…</Muted>
  if (error) return <Alert tone="danger">{errMsg(error, 'Falha ao carregar.')}</Alert>
  if (empty) return <Empty>{emptyText}</Empty>
  return <>{children}</>
}

export function DpPage() {
  const { canEdit, canView, isRH } = useAuth()
  const qc = useQueryClient()
  const [tab, setTab] = useState<DpTab>('resumo')
  const [competencia, setCompetencia] = useState(competenciaAtual)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'danger' | 'info'>('info')

  const folhaEdit = canEdit('dp-workflow-folha') || canEdit('departamento') || isRH

  const dash = useQuery({
    queryKey: ['dp', 'dashboard'],
    queryFn: fetchDpDashboard,
  })

  const folhas = useQuery({
    queryKey: ['dp', 'folhas'],
    queryFn: fetchFolhas,
    enabled: tab === 'folhas' || tab === 'resumo',
  })

  const ferias = useQuery({
    queryKey: ['dp', 'ferias'],
    queryFn: () => fetchFerias(),
    enabled: tab === 'ferias',
  })

  const rescisoes = useQuery({
    queryKey: ['dp', 'rescisoes'],
    queryFn: fetchRescisoes,
    enabled: tab === 'rescisoes' && canView('dp-rescisao'),
  })

  const invalidateFolha = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['dp', 'folhas'] }),
      qc.invalidateQueries({ queryKey: ['dp', 'dashboard'] }),
    ])
  }

  const abrirMut = useMutation({
    mutationFn: () => abrirFolha(competencia),
    onSuccess: async (res) => {
      setMsgTone('success')
      setMsg(res.mensagem || `Folha ${competencia} ${res.criada ? 'aberta' : 'já existia'}.`)
      await invalidateFolha()
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(errMsg(err, 'Falha ao abrir folha.'))
    },
  })

  const calcMut = useMutation({
    mutationFn: (id: string) => calcularFolha(id),
    onSuccess: async (res) => {
      setMsgTone('success')
      setMsg(res.mensagem || 'Folha calculada.')
      await invalidateFolha()
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(errMsg(err, 'Falha ao calcular folha.'))
    },
  })

  const aprovarMut = useMutation({
    mutationFn: (id: string) => aprovarFolha(id),
    onSuccess: async () => {
      setMsgTone('success')
      setMsg('Folha aprovada/fechada.')
      await invalidateFolha()
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(errMsg(err, 'Falha ao aprovar folha.'))
    },
  })

  const busy = abrirMut.isPending || calcMut.isPending || aprovarMut.isPending

  const encargosTop = useMemo(() => {
    const list = [...(dash.data?.encargos || [])]
    return list
      .sort((a, b) => (b.total_salarios || 0) - (a.total_salarios || 0))
      .slice(0, 8)
  }, [dash.data])

  const colab = dash.data?.colaboradores || {}

  const tabs: { id: DpTab; label: string }[] = [
    { id: 'resumo', label: 'Resumo' },
    { id: 'folhas', label: 'Folhas' },
    { id: 'ferias', label: 'Férias' },
    ...(canView('dp-rescisao') ? [{ id: 'rescisoes' as const, label: 'Rescisões' }] : []),
  ]

  const folhaLista = folhas.data?.dados || []
  const feriasLista = ferias.data?.dados || []
  const rescisoesLista = rescisoes.data?.dados || []

  return (
    <section className="hi-page">
      <PageHeader
        title="Departamento Pessoal"
        subtitle="Painel operacional: folha, férias e rescisões conectados à API."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/departamento')}>
              DP legado
            </a>
            {canEdit('dp-ferias') || canView('dp-ferias') ? (
              <Link className="btn btn-secondary" to="/app/dp/ferias">
                Férias
              </Link>
            ) : null}
            {canView('dp-rescisao') ? (
              <Link className="btn btn-primary" to="/app/dp/rescisao">
                Rescisão
              </Link>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <Tabs items={tabs} value={tab} onChange={setTab} label="Abas do DP" />

      {tab === 'resumo' ? (
        <div className="hi-stack">
          {dash.isLoading ? <Muted>Carregando indicadores…</Muted> : null}
          {dash.isError ? (
            <Alert tone="danger">{errMsg(dash.error, 'Falha ao carregar dashboard.')}</Alert>
          ) : null}
          {dash.data ? (
            <>
              <KpiGrid>
                <Kpi label="Colaboradores ativos" value={num(colab.ativos)} />
                <Kpi label="Admissões no mês" value={num(colab.admitidos_mes)} />
                <Kpi label="Em férias" value={num(colab.em_ferias)} />
                <Kpi label="Afastados" value={num(colab.afastados)} />
                <Kpi label="Férias pendentes" value={num(dash.data.ferias_pendentes)} />
                <Kpi label="Férias vencendo (60d)" value={num(dash.data.ferias_vencendo_60)} />
                <Kpi label="Rescisões abertas" value={num(dash.data.rescisoes_abertas)} />
                <Kpi label="Alertas críticos" value={num(dash.data.notificacoes_criticas)} />
              </KpiGrid>

              <Section title="Folha do mês">
                {dash.data.folhaMes ? (
                  <KpiGrid>
                    <Kpi
                      label="Competência"
                      value={formatCompetencia(String(dash.data.folhaMes.competencia || ''))}
                    />
                    <Kpi
                      label="Status"
                      value={String(
                        dash.data.folhaMes.status_display || dash.data.folhaMes.status || '-',
                      )}
                    />
                    <Kpi label="Bruto" value={formatMoney(dash.data.folhaMes.total_bruto as number)} />
                    <Kpi
                      label="Líquido"
                      value={formatMoney(dash.data.folhaMes.total_liquido as number)}
                    />
                  </KpiGrid>
                ) : (
                  <Empty>Nenhuma folha aberta para a competência atual.</Empty>
                )}
              </Section>

              <Section title="Encargos estimados por departamento" flush>
                {encargosTop.length === 0 ? (
                  <div className="card-body">
                    <Empty>Sem dados de encargos.</Empty>
                  </div>
                ) : (
                  <DataTable>
                    <thead>
                      <tr>
                        <th>Departamento</th>
                        <th>Headcount</th>
                        <th>Salários</th>
                        <th>Encargos est.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {encargosTop.map((row) => (
                        <tr key={row.departamento}>
                          <td>{row.departamento}</td>
                          <td>{row.colaboradores}</td>
                          <td>{formatMoney(row.total_salarios)}</td>
                          <td>{formatMoney(row.total_encargos_estimado)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                )}
              </Section>
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'folhas' ? (
        <div className="hi-stack">
          {folhaEdit ? (
            <Section title="Abrir competência">
              <form
                className="hi-filters"
                onSubmit={(e) => {
                  e.preventDefault()
                  abrirMut.mutate()
                }}
              >
                <input
                  type="month"
                  value={competencia}
                  onChange={(e) => setCompetencia(e.target.value)}
                  aria-label="Competência"
                  required
                />
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  Abrir / reabrir
                </button>
                <Link className="btn btn-secondary" to="/app/dp/massa">
                  Cálculo em massa
                </Link>
              </form>
            </Section>
          ) : null}

          <Section title="Folhas recentes" flush>
            <TableState
              loading={folhas.isLoading}
              error={folhas.isError ? folhas.error : null}
              empty={folhaLista.length === 0}
              emptyText="Nenhuma folha cadastrada ainda."
            >
              <DataTable>
                <thead>
                  <tr>
                    <th>Competência</th>
                    <th>Status</th>
                    <th>Colaboradores</th>
                    <th>Bruto</th>
                    <th>Líquido</th>
                    {folhaEdit ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {folhaLista.map((f) => {
                    const st = String(f.status || '').toLowerCase()
                    const podeCalcular = /aberta|rascunho|calculada/.test(st) && !/aprov|pag/.test(st)
                    const podeAprovar = st === 'calculada'
                    return (
                      <tr key={f.id}>
                        <td>{formatCompetencia(f.competencia)}</td>
                        <td>
                          <StatusBadge tone={statusTone(f.status)}>
                            {f.status_display || f.status || '-'}
                          </StatusBadge>
                        </td>
                        <td>{f.qtd_colaboradores ?? '-'}</td>
                        <td>{formatMoney(f.total_bruto)}</td>
                        <td>{formatMoney(f.total_liquido)}</td>
                        {folhaEdit ? (
                          <td>
                            <div className="hi-filters" style={{ margin: 0, gap: 6 }}>
                              {podeCalcular ? (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  disabled={busy}
                                  onClick={() => calcMut.mutate(f.id)}
                                >
                                  Calcular
                                </button>
                              ) : null}
                              {podeAprovar ? (
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  disabled={busy}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Aprovar/fechar folha ${formatCompetencia(f.competencia)}?`,
                                      )
                                    ) {
                                      aprovarMut.mutate(f.id)
                                    }
                                  }}
                                >
                                  Aprovar
                                </button>
                              ) : null}
                              {!podeCalcular && !podeAprovar ? '—' : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
            </TableState>
          </Section>
        </div>
      ) : null}

      {tab === 'ferias' ? (
        <Section title="Solicitações de férias" flush>
          <TableState
            loading={ferias.isLoading}
            error={ferias.isError ? ferias.error : null}
            empty={feriasLista.length === 0}
            emptyText="Nenhuma solicitação de férias."
          >
            <DataTable>
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Período</th>
                  <th>Dias</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {feriasLista.map((f) => (
                  <tr key={f.id}>
                    <td>{f.colaborador_nome || '-'}</td>
                    <td>
                      {formatDate(f.data_inicio)} → {formatDate(f.data_fim)}
                    </td>
                    <td>{f.dias ?? '-'}</td>
                    <td>
                      <StatusBadge tone={statusTone(f.status)}>
                        {f.status_display || f.status || '-'}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableState>
        </Section>
      ) : null}

      {tab === 'rescisoes' ? (
        <Section title="Rescisões" flush>
          <TableState
            loading={rescisoes.isLoading}
            error={rescisoes.isError ? rescisoes.error : null}
            empty={rescisoesLista.length === 0}
            emptyText="Nenhuma rescisão registrada."
          >
            <DataTable>
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Tipo</th>
                  <th>Demissão</th>
                  <th>Líquido</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rescisoesLista.map((r) => (
                  <tr key={r.id}>
                    <td>{r.colaborador_nome || '-'}</td>
                    <td>{r.tipo_display || r.tipo || '-'}</td>
                    <td>{formatDate(r.data_demissao)}</td>
                    <td>{formatMoney(r.valor_liquido)}</td>
                    <td>
                      <StatusBadge tone={statusTone(r.status)}>
                        {r.status_display || r.status || '-'}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableState>
        </Section>
      ) : null}
    </section>
  )
}

export default DpPage
